/**
 * @file useDatePickerEx.js
 * @description フォーム日付ピッカーフィールドを簡単に生成するためのカスタムフック
 *
 * このフックは、Material-UIのコンポーネントを使用して、
 * スタイリッシュで機能的な日付ピッカーフィールドを提供します。
 * データベーススキーマから自動生成された日付型カラムに対応した
 * 日付ピッカーフィールドを簡単に実装できるように設計されています。
 *
 * @example
 * // 基本的な使用例
 * const [form, formProps] = useFormEx();
 * const { datePicker_created_at, datePicker_updated_at } = useDatePickerEx(form, formProps);
 *
 * // コンポーネント内での使用
 * return (
 *   <form>
 *     {datePicker_created_at({
 *       name: "createdAt",
 *       label: "作成日時",
 *       required: true
 *     })}
 *   </form>
 * );
 *
 * @author MetroJS Team
 * @version 1.0.0
 * @since 2024-03-01
 */

"use client";

import logjs from "@metrojs/logjs";
import dayjs from "@metrojs/dayjs";
const log = new logjs("useDatePickerEx");
const baseStyle = {
  width: "160px",
  padding: "3px 5px",
  border: "1px solid #ccc",
  textAlign: "center",
  borderRadius: "3px",
  maxLength: 10,
}; // ベーススタイル
/**
 * フォーム日付ピッカーフィールドを生成するためのカスタムフック
 * @param {Object} form - フォームデータオブジェクト
 * @param {Object} formProps - フォームプロパティオブジェクト
 * @returns {Object} 各種日付ピッカーフィールド生成関数のオブジェクト
 */
const useDatePickerEx = (form = {}, formProps = {}) => {
  const is_standard = true; // 標準の日付ピッカーを使用するかどうか

  /**
   * 回答日時日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 回答日時日付ピッカーフィールドのJSX要素
   */
  const datePicker_LimitDate = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_LimitDate form.limitDate:${form?.limitDate || ""} params:`, params);

    // disableFutureプロパティを適切に処理
    const { disableFuture, ...otherParams } = params;

    return formProps.datePicker(
      "limitDate",
      {
        label: "回答日時",
        is_standard,
        disablePast: true,
        style: { ...baseStyle, ...params?.style },
        ...(disableFuture !== undefined && { disableFuture }),
        ...otherParams,
      },
      is_debug
    );
  };

  /**
   * 締日　重複なし日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 締日　重複なし日付ピッカーフィールドのJSX要素
   */
  const datePicker_Wellsetday = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Wellsetday form.Wellsetday:${form?.Wellsetday || ""} params:`, params);
    return formProps.datePicker("Wellsetday", { label: "締日　重複なし", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 締対象開始日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 締対象開始日日付ピッカーフィールドのJSX要素
   */
  const datePicker_Wsd_Start = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Wsd_Start form.Wsd_Start:${form?.Wsd_Start || ""} params:`, params);
    return formProps.datePicker("Wsd_Start", { label: "締対象開始日", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 締対象終了日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 締対象終了日日付ピッカーフィールドのJSX要素
   */
  const datePicker_Wsd_End = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Wsd_End form.Wsd_End:${form?.Wsd_End || ""} params:`, params);
    return formProps.datePicker("Wsd_End", { label: "締対象終了日", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 最終閲覧日時日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 最終閲覧日時日付ピッカーフィールドのJSX要素
   */
  const datePicker_dl_date = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_dl_date form.dl_date:${form?.dl_date || ""} params:`, params);
    return formProps.datePicker("dl_date", { label: "最終閲覧日時", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 作成日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 作成日日付ピッカーフィールドのJSX要素
   */
  const datePicker_created = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_created form.created:${form?.created || ""} params:`, params);
    return formProps.datePicker("created", { label: "作成日", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 更新日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 更新日日付ピッカーフィールドのJSX要素
   */
  const datePicker_modified = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_modified form.modified:${form?.modified || ""} params:`, params);
    return formProps.datePicker("modified", { label: "更新日", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 成約日時日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 成約日時日付ピッカーフィールドのJSX要素
   */
  const datePicker_Seiyaku_Date = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Seiyaku_Date form.Seiyaku_Date:${form?.Seiyaku_Date || ""} params:`, params);
    return formProps.datePicker("Seiyaku_Date", { label: "成約日時", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * FAX送付日時日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} FAX送付日時日付ピッカーフィールドのJSX要素
   */
  const datePicker_Fax_Date = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Fax_Date form.Fax_Date:${form?.Fax_Date || ""} params:`, params);
    return formProps.datePicker("Fax_Date", { label: "FAX送付日時", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 入金確認日時日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 入金確認日時日付ピッカーフィールドのJSX要素
   */
  const datePicker_Nyukin_Date = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Nyukin_Date form.Nyukin_Date:${form?.Nyukin_Date || ""} params:`, params);
    return formProps.datePicker("Nyukin_Date", { label: "入金確認日時", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 書類受取日時日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 書類受取日時日付ピッカーフィールドのJSX要素
   */
  const datePicker_ShoruiUketori_Date = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_ShoruiUketori_Date form.ShoruiUketori_Date:${form?.ShoruiUketori_Date || ""} params:`, params);
    return formProps.datePicker("ShoruiUketori_Date", { label: "書類受取日時", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 書類送付日時日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 書類送付日時日付ピッカーフィールドのJSX要素
   */
  const datePicker_ShoruiSoufu_Date = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_ShoruiSoufu_Date form.ShoruiSoufu_Date:${form?.ShoruiSoufu_Date || ""} params:`, params);
    return formProps.datePicker("ShoruiSoufu_Date", { label: "書類送付日時", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 搬出日時日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 搬出日時日付ピッカーフィールドのJSX要素
   */
  const datePicker_Hanshutsu_Date = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Hanshutsu_Date form.Hanshutsu_Date:${form?.Hanshutsu_Date || ""} params:`, params);
    return formProps.datePicker("Hanshutsu_Date", { label: "搬出日時", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 引渡日時日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 引渡日時日付ピッカーフィールドのJSX要素
   */
  const datePicker_Hikiwatashi_Date = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Hikiwatashi_Date form.Hikiwatashi_Date:${form?.Hikiwatashi_Date || ""} params:`, params);
    return formProps.datePicker("Hikiwatashi_Date", { label: "引渡日時", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 引渡日登録日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 引渡日登録日日付ピッカーフィールドのJSX要素
   */
  const datePicker_Hikiwatashi_Input_Date = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Hikiwatashi_Input_Date form.Hikiwatashi_Input_Date:${form?.Hikiwatashi_Input_Date || ""} params:`, params);
    return formProps.datePicker("Hikiwatashi_Input_Date", { label: "引渡日登録日", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 支払日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 支払日日付ピッカーフィールドのJSX要素
   */
  const datePicker_Shiharai_Date = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Shiharai_Date form.Shiharai_Date:${form?.Shiharai_Date || ""} params:`, params);
    return formProps.datePicker("Shiharai_Date", { label: "支払日", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 本部処理更新日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 本部処理更新日日付ピッカーフィールドのJSX要素
   */
  const datePicker_LastupDate = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_LastupDate form.LastupDate:${form?.LastupDate || ""} params:`, params);
    return formProps.datePicker("LastupDate", { label: "本部処理更新日", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 日付ピッカーフィールドのJSX要素
   */
  const datePicker_WellsetDate = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_WellsetDate form.WellsetDate:${form?.WellsetDate || ""} params:`, params);
    return formProps.datePicker("WellsetDate", { label: "", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 更新日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 更新日日付ピッカーフィールドのJSX要素
   */
  const datePicker_mofified = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_mofified form.mofified:${form?.mofified || ""} params:`, params);
    return formProps.datePicker("mofified", { label: "更新日", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * メール送信日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} メール送信日日付ピッカーフィールドのJSX要素
   */
  const datePicker_MailDate = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_MailDate form.MailDate:${form?.MailDate || ""} params:`, params);
    return formProps.datePicker("MailDate", { label: "メール送信日", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 整備メール送信日時日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 整備メール送信日時日付ピッカーフィールドのJSX要素
   */
  const datePicker_abMailDate = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_abMailDate form.abMailDate:${form?.abMailDate || ""} params:`, params);
    return formProps.datePicker("abMailDate", { label: "整備メール送信日時", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 生産者メール送信日時日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 生産者メール送信日時日付ピッカーフィールドのJSX要素
   */
  const datePicker_yzMailDate = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_yzMailDate form.yzMailDate:${form?.yzMailDate || ""} params:`, params);
    return formProps.datePicker("yzMailDate", { label: "生産者メール送信日時", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 支払日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 支払日日付ピッカーフィールドのJSX要素
   */
  const datePicker_Expense_day = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Expense_day form.Expense_day:${form?.Expense_day || ""} params:`, params);
    return formProps.datePicker("Expense_day", { label: "支払日", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 手形期日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 手形期日日付ピッカーフィールドのJSX要素
   */
  const datePicker_Expense_Kijitsu = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Expense_Kijitsu form.Expense_Kijitsu:${form?.Expense_Kijitsu || ""} params:`, params);
    return formProps.datePicker("Expense_Kijitsu", { label: "手形期日", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 入金日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 入金日日付ピッカーフィールドのJSX要素
   */
  const datePicker_Pay_day = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Pay_day form.Pay_day:${form?.Pay_day || ""} params:`, params);
    return formProps.datePicker("Pay_day", { label: "入金日", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 手形期日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 手形期日日付ピッカーフィールドのJSX要素
   */
  const datePicker_Pay_Kijitsu = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Pay_Kijitsu form.Pay_Kijitsu:${form?.Pay_Kijitsu || ""} params:`, params);
    return formProps.datePicker("Pay_Kijitsu", { label: "手形期日", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 発行日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 発行日日付ピッカーフィールドのJSX要素
   */
  const datePicker_CommitDate = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_CommitDate form.CommitDate:${form?.CommitDate || ""} params:`, params);
    return formProps.datePicker("CommitDate", { label: "発行日", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 消し込み日(入金日)日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 消し込み日(入金日)日付ピッカーフィールドのJSX要素
   */
  const datePicker_NyukinDate = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_NyukinDate form.NyukinDate:${form?.NyukinDate || ""} params:`, params);
    return formProps.datePicker("NyukinDate", { label: "消し込み日(入金日)", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 支払日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 支払日日付ピッカーフィールドのJSX要素
   */
  const datePicker_ShiharaiDate = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_ShiharaiDate form.ShiharaiDate:${form?.ShiharaiDate || ""} params:`, params);
    return formProps.datePicker("ShiharaiDate", { label: "支払日", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 注文日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 注文日日付ピッカーフィールドのJSX要素
   */
  const datePicker_OrderDate_Start = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_OrderDate_Start form.OrderDate:${form?.OrderDate || ""} params:`, params);
    return formProps.datePicker("OrderDate_Start", { label: "注文日", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  const datePicker_OrderDate_End = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_OrderDate_End form.OrderDate:${form?.OrderDate || ""} params:`, params);
    return formProps.datePicker("OrderDate_End", { label: "注文日", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 返品日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 返品日日付ピッカーフィールドのJSX要素
   */
  const datePicker_ReturnDate = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_ReturnDate form.ReturnDate:${form?.ReturnDate || ""} params:`, params);
    return formProps.datePicker("ReturnDate", { label: "返品日", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 請求書/計算書表示用日付日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 請求書/計算書表示用日付日付ピッカーフィールドのJSX要素
   */
  const datePicker_ViewDate = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_ViewDate form.ViewDate:${form?.ViewDate || ""} params:`, params);
    return formProps.datePicker("ViewDate", { label: "請求書/計算書表示用日付", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 別締め日用締処理実行日(商工組合、イチネン等)日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 別締め日用締処理実行日(商工組合、イチネン等)日付ピッカーフィールドのJSX要素
   */
  const datePicker_SBSKDate = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_SBSKDate form.SBSKDate:${form?.SBSKDate || ""} params:`, params);
    return formProps.datePicker("SBSKDate", { label: "別締め日用締処理実行日(商工組合、イチネン等)", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 有効期限日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 有効期限日付ピッカーフィールドのJSX要素
   */
  const datePicker_ExpiredDate = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_ExpiredDate form.ExpiredDate:${form?.ExpiredDate || ""} params:`, params);
    return formProps.datePicker("ExpiredDate", { label: "有効期限", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 車輌：引取予定日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：引取予定日日付ピッカーフィールドのJSX要素
   */
  const datePicker_C_HikitoriYoteibi = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_C_HikitoriYoteibi form.C_HikitoriYoteibi:${form?.C_HikitoriYoteibi || ""} params:`, params);
    return formProps.datePicker("C_HikitoriYoteibi", { label: "車輌：引取予定日", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 車輌：引取希望日(日付)日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：引取希望日(日付)日付ピッカーフィールドのJSX要素
   */
  const datePicker_C_HikitoriKiboubi = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_C_HikitoriKiboubi form.C_HikitoriKiboubi:${form?.C_HikitoriKiboubi || ""} params:`, params);
    return formProps.datePicker("C_HikitoriKiboubi", { label: "車輌：引取希望日(日付)", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 車輌：引渡日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：引渡日日付ピッカーフィールドのJSX要素
   */
  const datePicker_C_Hikiwatashibi = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_C_Hikiwatashibi form.C_Hikiwatashibi:${form?.C_Hikiwatashibi || ""} params:`, params);
    return formProps.datePicker("C_Hikiwatashibi", { label: "車輌：引渡日", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 見積有効期限日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 見積有効期限日付ピッカーフィールドのJSX要素
   */
  const datePicker_Sell_Limit = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Sell_Limit form.Sell_Limit:${form?.Sell_Limit || ""} params:`, params);
    return formProps.datePicker("Sell_Limit", { label: "見積有効期限", disablePast: true, is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  const datePicker_Sell_Limit_Start = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Sell_Limit_Start form.Sell_Limit:${form?.Sell_Limit || ""} params:`, params);
    return formProps.datePicker("Sell_Limit_Start", { label: "見積有効期限", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  const datePicker_Sell_Limit_End = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Sell_Limit_End form.Sell_Limit:${form?.Sell_Limit || ""} params:`, params);
    return formProps.datePicker("Sell_Limit_End", { label: "見積有効期限", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 在庫回答期限日時日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 在庫回答期限日時日付ピッカーフィールドのJSX要素
   */
  const datePicker_Ans_Limit = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Ans_Limit form.Ans_Limit:${form?.Ans_Limit || ""} params:`, params);
    return formProps.datePicker("Ans_Limit", { label: "在庫回答期限日時", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 注文日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 注文日日付ピッカーフィールドのJSX要素
   */
  const datePicker_Chumon_Date = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Chumon_Date form.Chumon_Date:${form?.Chumon_Date || ""} params:`, params);
    return formProps.datePicker("Chumon_Date", { label: "注文日", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 受注日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 受注日日付ピッカーフィールドのJSX要素
   */
  const datePicker_OrderRec_Start = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_OrderRec_Start form.OrderRec_Date:${form?.OrderRec_Date || ""} params:`, params);
    return formProps.datePicker("OrderRec_Start", { label: "受注日", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  const datePicker_OrderRec_End = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_OrderRec_End form.OrderRec_Date:${form?.OrderRec_Date || ""} params:`, params);
    return formProps.datePicker("OrderRec_End", { label: "受注日", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };

  /**
   * 発送日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 発送日日付ピッカーフィールドのJSX要素
   */
  const datePicker_Haiso_Date = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Haiso_Date form.Haiso_Date:${form?.Haiso_Date || ""} params:`, params);
    return formProps.datePicker("Haiso_Date", { label: "発送日", is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 発送予定日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 発送予定日付ピッカーフィールドのJSX要素
   */
  const datePicker_Haiso_Yotei = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Haiso_Yotei form.Haiso_Yotei:${form?.Haiso_Yotei || ""} params:`, params);
    return formProps.datePicker("Haiso_Yotei", { label: "発送予定", disablePast: true, is_standard, style: { ...baseStyle, ...params?.style }, ...params }, is_debug);
  };
  /**
   * 見積依頼日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 見積依頼日日付ピッカーフィールドのJSX要素
   */
  const datePicker_Request_Start = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Request_Start form.Request_Start:${form?.Request_Start || ""} params:`, params);

    // disableFuture/disablePast を適切に処理
    const { disableFuture, disablePast, ...otherParams } = params;

    return formProps.datePicker(
      "Request_Start",
      {
        label: "見積依頼日",
        is_standard,
        maxLength: 10,
        style: { ...baseStyle, size: "10px", ...params?.style },
        // disableFutureは適切に処理
        ...(disableFuture !== undefined && { disableFuture }),
        // disablePastはminDateに変換して処理
        ...(disablePast ? { minDate: dayjs().startOf("day") } : {}),
        // otherParamsからdisablePastを除外
        ...otherParams,
      },
      is_debug
    );
  };

  const datePicker_Request_End = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Request_End form.Request_End:${form?.Request_End || ""} params:`, params);

    // disableFutureプロパティを適切に処理
    const { disableFuture, ...otherParams } = params;

    return formProps.datePicker(
      "Request_End",
      {
        label: "見積依頼日",
        is_standard,
        maxLength: 10,
        style: { ...baseStyle, size: "10px", ...params?.style },
        ...(disableFuture !== undefined && { disableFuture }),
        ...otherParams,
      },
      is_debug
    );
  };
  /**
   * 見積依頼期限日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 見積依頼期限日付ピッカーフィールドのJSX要素
   */
  const datePicker_Request_Limit_Start = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Request_Limit_Start form.Request_Limit:${form?.Request_Limit || ""} params:`, params);
    // disableFuture/disablePast を適切に処理
    const { disableFuture, disablePast, ...otherParams } = params;

    return formProps.datePicker(
      "Request_Limit_Start",
      {
        label: "見積依頼期限",
        is_standard,
        style: { ...baseStyle, ...params?.style },
        // disableFutureは適切に処理
        ...(disableFuture !== undefined && { disableFuture }),
        // disablePastはminDateに変換して処理
        ...(disablePast ? { minDate: dayjs().startOf("day") } : {}),
        // otherParamsからdisablePastを除外
        ...otherParams,
      },
      is_debug
    );
  };
  const datePicker_Request_Limit_End = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Request_Limit_End form.Request_Limit:${form?.Request_Limit || ""} params:`, params);
    return formProps.datePicker("Request_Limit_End", { label: "見積依頼期限", is_standard, style: { ...baseStyle, ...params.style }, ...params }, is_debug);
  };

  /**
   * 見積回答日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 見積回答日日付ピッカーフィールドのJSX要素
   */
  const datePicker_Answer_Start = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Answer_Start form.Answer_Start:${form?.Answer_Start || ""} params:`, params);
    return formProps.datePicker("Answer_Start", { label: "見積回答日", is_standard, style: { ...baseStyle, ...params.style }, ...params }, is_debug);
  };
  const datePicker_Answer_End = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Answer_End form.Answer_End:${form?.Answer_End || ""} params:`, params);
    return formProps.datePicker("Answer_End", { label: "見積回答日", is_standard, style: { ...baseStyle, ...params.style }, ...params }, is_debug);
  };
  /**
   * 掲載開始日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 掲載開始日日付ピッカーフィールドのJSX要素
   */
  const datePicker_Startdate = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Startdate form.Startdate:${form?.Startdate || ""} params:`, params);
    return formProps.datePicker("Startdate", { label: "掲載開始日", is_standard, style: { ...baseStyle, ...params.style }, ...params }, is_debug);
  };
  /**
   * 掲載終了日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 掲載終了日日付ピッカーフィールドのJSX要素
   */
  const datePicker_Enddate = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_Enddate form.Enddate:${form?.Enddate || ""} params:`, params);
    return formProps.datePicker("Enddate", { label: "掲載終了日", is_standard, style: { ...baseStyle, ...params.style }, ...params }, is_debug);
  };
  /**
   * 税率適用開始日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 税率適用開始日日付ピッカーフィールドのJSX要素
   */
  const datePicker_StartDate = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_StartDate form.StartDate:${form?.StartDate || ""} params:`, params);
    return formProps.datePicker("StartDate", { label: "税率適用開始日", is_standard, style: { ...baseStyle, ...params.style }, ...params }, is_debug);
  };
  /**
   * 税率適用終了日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 税率適用終了日日付ピッカーフィールドのJSX要素
   */
  const datePicker_EndDate = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_EndDate form.EndDate:${form?.EndDate || ""} params:`, params);
    return formProps.datePicker("EndDate", { label: "税率適用終了日", is_standard, style: { ...baseStyle, ...params.style }, ...params }, is_debug);
  };
  /**
   * 登録日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 登録日日付ピッカーフィールドのJSX要素
   */
  const datePicker_RegDate = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_RegDate form.RegDate:${form?.RegDate || ""} params:`, params);
    return formProps.datePicker("RegDate", { label: "登録日", is_standard, style: { ...baseStyle, ...params.style }, ...params }, is_debug);
  };
  /**
   * 保管予定期間日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 保管予定期間日付ピッカーフィールドのJSX要素
   */
  const datePicker_HokanKikan = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_HokanKikan form.HokanKikan:${form?.HokanKikan || ""} params:`, params);
    return formProps.datePicker("HokanKikan", { label: "保管予定期間", is_standard, style: { ...baseStyle, ...params.style }, ...params }, is_debug);
  };
  /**
   * 入庫予定日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 入庫予定日日付ピッカーフィールドのJSX要素
   */
  const datePicker_NyukoYotei = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_NyukoYotei form.NyukoYotei:${form?.NyukoYotei || ""} params:`, params);
    return formProps.datePicker("NyukoYotei", { label: "入庫予定日", is_standard, style: { ...baseStyle, ...params.style }, ...params }, is_debug);
  };
  /**
   * 入庫日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 入庫日日付ピッカーフィールドのJSX要素
   */
  const datePicker_NyukoDate = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_NyukoDate form.NyukoDate:${form?.NyukoDate || ""} params:`, params);
    return formProps.datePicker("NyukoDate", { label: "入庫日", is_standard, style: { ...baseStyle, ...params.style }, ...params }, is_debug);
  };
  /**
   * ログイン日時日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} ログイン日時日付ピッカーフィールドのJSX要素
   */
  const datePicker_LoginDate = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_LoginDate form.LoginDate:${form?.LoginDate || ""} params:`, params);
    return formProps.datePicker("LoginDate", { label: "ログイン日時", is_standard, style: { ...baseStyle, ...params.style }, ...params }, is_debug);
  };
  /**
   * キャンペーン開始日日付ピッカーフィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} キャンペーン開始日日付ピッカーフィールドのJSX要素
   */
  const datePicker_CampaignDate = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`datePicker_CampaignDate form.CampaignDate:${form?.CampaignDate || ""} params:`, params);
    return formProps.datePicker("CampaignDate", { label: "キャンペーン開始日", is_standard, style: { ...baseStyle, ...params.style }, ...params }, is_debug);
  };

  return {
    datePicker_Wellsetday,
    datePicker_Wsd_Start,
    datePicker_Wsd_End,
    datePicker_dl_date,
    datePicker_created,
    datePicker_modified,
    datePicker_Seiyaku_Date,
    datePicker_Fax_Date,
    datePicker_Nyukin_Date,
    datePicker_ShoruiUketori_Date,
    datePicker_ShoruiSoufu_Date,
    datePicker_Hanshutsu_Date,
    datePicker_Hikiwatashi_Date,
    datePicker_Hikiwatashi_Input_Date,
    datePicker_Shiharai_Date,
    datePicker_LastupDate,
    datePicker_WellsetDate,
    datePicker_mofified,
    datePicker_MailDate,
    datePicker_abMailDate,
    datePicker_yzMailDate,
    datePicker_Expense_day,
    datePicker_Expense_Kijitsu,
    datePicker_Pay_day,
    datePicker_Pay_Kijitsu,
    datePicker_CommitDate,
    datePicker_NyukinDate,
    datePicker_ShiharaiDate,
    datePicker_OrderDate_Start,
    datePicker_OrderDate_End,
    datePicker_ReturnDate,
    datePicker_ViewDate,
    datePicker_SBSKDate,
    datePicker_ExpiredDate,
    datePicker_C_HikitoriYoteibi,
    datePicker_C_HikitoriKiboubi,
    datePicker_C_Hikiwatashibi,
    datePicker_Sell_Limit,
    datePicker_Sell_Limit_Start,
    datePicker_Sell_Limit_End,
    datePicker_Ans_Limit,
    datePicker_Chumon_Date,
    datePicker_OrderRec_Start,
    datePicker_OrderRec_End,
    datePicker_Haiso_Date,
    datePicker_Haiso_Yotei,
    datePicker_Request_Start,
    datePicker_Request_End,
    datePicker_Request_Limit_Start,
    datePicker_Request_Limit_End,
    datePicker_Answer_Start,
    datePicker_Answer_End,
    datePicker_Startdate,
    datePicker_Enddate,
    datePicker_StartDate,
    datePicker_EndDate,
    datePicker_RegDate,
    datePicker_HokanKikan,
    datePicker_NyukoYotei,
    datePicker_NyukoDate,
    datePicker_LoginDate,
    datePicker_CampaignDate,
    datePicker_LimitDate,
  };
};

export default useDatePickerEx;
